/**
 * Client for `/api/mo/*`.
 *
 * The grouping and formatting logic lives here as pure functions rather than in
 * the components, so it can be tested directly. The components stay thin enough
 * that a smoke render is sufficient coverage for them.
 */

import type { Dashboard, DashboardCommitment } from '@rbv/shared';

export type AttentionKind =
  | 'commitment-due'
  | 'commitment-waiting'
  | 'unresolved-identity'
  | 'low-confidence'
  | 'ingest-health';

export interface AttentionItem {
  id: number;
  kind: AttentionKind;
  title: string;
  ref: string;
  createdAt: string;
  readAt: string | null;
  resolvedAt: string | null;
}

export interface CommitmentView {
  text: string;
  path: string;
  owedBy: string;
  owedTo: string;
  due: string;
  blockedOn: string;
  topic: string;
}

export interface CounterpartyView {
  name: string;
  owedByOwner: CommitmentView[];
  owedToOwner: CommitmentView[];
}

export interface TopicView {
  name: string;
  path: string;
  status: string;
  summary: string;
  participants: string[];
}

export interface Briefing {
  counterparties: CounterpartyView[];
  overdue: CommitmentView[];
  activeTopics: TopicView[];
  limitations: string[];
}

export interface Dossier {
  name: string;
  path: string;
  vantageId: string;
  role: string;
  emails: string[];
  aliases: string[];
  referredBy: string;
  resolution: string;
  owedByOwner: CommitmentView[];
  owedToOwner: CommitmentView[];
  topics: TopicView[];
  decisions: { text: string; path: string; decidedBy: string; topic: string }[];
}

export interface MoStatus {
  enabled: boolean;
  lastIngestRun: string | null;
  stalled: boolean;
  openItems: number;
}

/**
 * Loading state as data. `disabled` is distinct from `error` on purpose: a
 * disabled feature is an expected state with a specific explanation, and showing
 * it as an error would send the owner debugging something that is working.
 */
export type Loaded<T> =
  | { state: 'loading' }
  | { state: 'disabled'; message: string }
  | { state: 'error'; message: string }
  | { state: 'ready'; value: T };

async function get<T>(path: string): Promise<Loaded<T>> {
  let res: Response;
  try {
    res = await fetch(path, { credentials: 'same-origin' });
  } catch (err) {
    return { state: 'error', message: (err as Error).message };
  }

  if (res.status === 503) {
    let message = 'MO OS ingestion is not enabled.';
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep the default */
    }
    return { state: 'disabled', message };
  }

  if (res.status === 404) return { state: 'error', message: 'not found' };
  if (!res.ok) return { state: 'error', message: `request failed with ${res.status}` };

  try {
    return { state: 'ready', value: (await res.json()) as T };
  } catch {
    return { state: 'error', message: 'response was not JSON' };
  }
}

export const fetchStatus = (): Promise<Loaded<MoStatus>> => get<MoStatus>('/api/mo/status');

export const fetchAttention = (): Promise<Loaded<{ items: AttentionItem[] }>> =>
  get<{ items: AttentionItem[] }>('/api/mo/attention');

export const fetchBriefing = (): Promise<Loaded<Briefing>> => get<Briefing>('/api/mo/briefing');

/**
 * The master dashboard payload. The shape lives in @rbv/shared because the
 * ordering is contract rather than a rendering choice, so both sides read the
 * same file.
 */
export const fetchDashboard = (): Promise<Loaded<Dashboard>> =>
  get<Dashboard>('/api/mo/dashboard');

export const fetchPerson = (name: string): Promise<Loaded<Dossier>> =>
  get<Dossier>(`/api/mo/person/${encodeURIComponent(name)}`);

async function post(path: string): Promise<boolean> {
  try {
    const res = await fetch(path, { method: 'POST', credentials: 'same-origin' });
    return res.ok;
  } catch {
    return false;
  }
}

export const resolveAttention = (id: number): Promise<boolean> =>
  post(`/api/mo/attention/${id}/resolve`);

export const markAttentionRead = (id: number): Promise<boolean> =>
  post(`/api/mo/attention/${id}/read`);

export interface GroupedFeed {
  /** Things the owner owes someone. */
  owe: AttentionItem[];
  /** Things the owner is waiting on from someone else. */
  waiting: AttentionItem[];
  /** Things needing a judgement call: identities and uncertain extractions. */
  decide: AttentionItem[];
  /** Ingest health, surfaced separately because it means the brain is blind. */
  health: AttentionItem[];
}

/**
 * Split the one feed into the sections the surface shows. Kept separate from the
 * server because the split is a presentation choice, and the server deliberately
 * owns a single feed with shared state.
 */
export function groupFeed(items: AttentionItem[]): GroupedFeed {
  return {
    owe: items.filter((i) => i.kind === 'commitment-due'),
    waiting: items.filter((i) => i.kind === 'commitment-waiting'),
    decide: items.filter((i) => i.kind === 'unresolved-identity' || i.kind === 'low-confidence'),
    health: items.filter((i) => i.kind === 'ingest-health'),
  };
}

/** A due date rendered relative to today, which is how the owner reads it. */
export function dueLabel(due: string, today = new Date().toISOString().slice(0, 10)): string {
  if (due === '') return '';
  if (due < today) return `overdue since ${due}`;
  if (due === today) return 'due today';
  return `due ${due}`;
}

/**
 * A commitment's deadline read off the server's day count rather than its date
 * string. Overdue has to read as overdue: the owner scans this list for what has
 * already slipped, and "due 2026-07-29" makes him do the subtraction himself.
 *
 * A null count means no date was extracted. Saying so beats implying a deadline
 * that nobody ever set.
 */
export function dueInDaysLabel(days: number | null, due = ''): string {
  if (days === null) return due === '' ? 'no due date' : `due ${due}`;
  if (days < 0) {
    const n = Math.abs(days);
    return `${n} ${n === 1 ? 'day' : 'days'} overdue`;
  }
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `due in ${days} days`;
}

/** Person name out of a node path like `mo/people/Rin Halvorsen.md`. */
export function personFromRef(ref: string): string {
  const m = ref.match(/^mo\/people\/(.+)\.md$/);
  return m ? m[1] : '';
}

// ---------------------------------------------------------------------------
// Person merges: the other half of an identity question.
// ---------------------------------------------------------------------------

export interface MergeCandidate {
  name: string;
  path: string;
  inboundLinks: number;
  emails: string[];
  hasBody: boolean;
  looksLikeVariant: boolean;
}

export interface IdentityGroup {
  email: string;
  candidates: MergeCandidate[];
  recommended: string;
  why: string;
  caution: boolean;
}

export interface MergePlan {
  survivor: string;
  tombstone: string;
  relinked: string[];
  summary: string[];
}

/**
 * Who claims the address behind one identity question.
 *
 * 409 is a real answer rather than a failure: it means the address is the owner's own,
 * so it groups strangers and there is nothing to merge. The message is worth showing,
 * which is why this does not collapse it into a generic error.
 */
export async function fetchIdentityGroup(
  ref: string,
): Promise<Loaded<IdentityGroup> | { state: 'refused'; message: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/mo/people/identity?ref=${encodeURIComponent(ref)}`, {
      credentials: 'same-origin',
    });
  } catch (err) {
    return { state: 'error', message: (err as Error).message };
  }
  if (res.status === 409 || res.status === 404) {
    let message = 'There is nothing to merge here.';
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep the default */
    }
    return { state: 'refused', message };
  }
  if (res.status === 503) return { state: 'disabled', message: 'MO OS ingestion is not enabled.' };
  if (!res.ok) return { state: 'error', message: `request failed with ${res.status}` };
  try {
    return { state: 'ready', value: (await res.json()) as IdentityGroup };
  } catch {
    return { state: 'error', message: 'response was not JSON' };
  }
}

/** POST with a JSON body, returning the parsed body or the server's own error text. */
async function postJson<T>(path: string, body: unknown): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    /* handled below */
  }
  if (!res.ok) {
    const error = (parsed as { error?: string } | null)?.error;
    // The server's reason is far more useful than the status: "a note cannot be merged
    // into itself" tells him what to change, where "400" does not.
    return { ok: false, error: error ?? `request failed with ${res.status}` };
  }
  return { ok: true, value: parsed as T };
}

/** What a merge WOULD do. Writes nothing. */
export const fetchMergePlan = (
  from: string,
  into: string,
): Promise<{ ok: true; value: MergePlan } | { ok: false; error: string }> =>
  postJson<MergePlan>('/api/mo/people/merge/plan', { from, into });

/** Perform it. Only ever called from the confirm step. */
export const applyMerge = (
  from: string,
  into: string,
): Promise<{ ok: true; value: { written: number } } | { ok: false; error: string }> =>
  postJson<{ written: number }>('/api/mo/people/merge', { from, into });

// ---------------------------------------------------------------------------
// Today's social drafts, from Postiz.
// ---------------------------------------------------------------------------

export interface SocialDraft {
  id: string;
  text: string;
  account: string;
  platform: string;
  publishAt: string;
  image: string | null;
  state: string;
}

export interface SocialToday {
  /** Waiting on him. */
  drafts: SocialDraft[];
  /** Going out later today unless he holds it. Not the same as published. */
  scheduled: SocialDraft[];
  published: SocialDraft[];
  failed: SocialDraft[];
  problems: string[];
}

/**
 * 501 means Postiz is not configured, which is a state to explain rather than an error to
 * report, so it comes back as `disabled` with the server's own message.
 */
export async function fetchSocialToday(): Promise<Loaded<SocialToday>> {
  let res: Response;
  try {
    res = await fetch('/api/mo/social/today', { credentials: 'same-origin' });
  } catch (err) {
    return { state: 'error', message: (err as Error).message };
  }
  if (res.status === 501 || res.status === 503) {
    let message = 'The social queue is not configured.';
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* keep the default */
    }
    return { state: 'disabled', message };
  }
  if (!res.ok) return { state: 'error', message: `request failed with ${res.status}` };
  try {
    const body = (await res.json()) as Partial<SocialToday> | null;
    /*
     * Normalised, not cast. A cast made the panel trust the shape and a 200 carrying
     * anything else crashed it on `drafts.length`, taking the whole dashboard down with it
     * because the panel renders inside the deck. The server side is already tolerant of
     * Postiz's payload for the same reason; the client owes the same care to its own.
     */
    const list = (v: unknown): SocialDraft[] => (Array.isArray(v) ? (v as SocialDraft[]) : []);
    return {
      state: 'ready',
      value: {
        drafts: list(body?.drafts),
        scheduled: list(body?.scheduled),
        published: list(body?.published),
        failed: list(body?.failed),
        problems: Array.isArray(body?.problems) ? body.problems : [],
      },
    };
  } catch {
    return { state: 'error', message: 'response was not JSON' };
  }
}

/** Post to a social action endpoint. Separate paths, never one parameterised call. */
async function socialAction(
  id: string,
  action: 'approve' | 'hold',
): Promise<{ ok: true } | { ok: false; error: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/mo/social/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  if (res.ok) return { ok: true };
  let error = `${action} failed with ${res.status}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) error = body.error;
  } catch {
    /* keep the default */
  }
  return { ok: false, error };
}

/** Hand one draft to Postiz's scheduler. The caller has already confirmed with him. */
export const approveSocialDraft = (id: string) => socialAction(id, 'approve');

/** Pull a queued post back to draft so it does not go out. Safe, so no confirm step. */
export const holdSocialPost = (id: string) => socialAction(id, 'hold');

/**
 * What approving will do, in words. Mirrors the server's `describeWhen`.
 *
 * Duplicated deliberately rather than shipped in the payload: the button label has to
 * stay truthful as the clock moves while he reads the panel, and a string baked at fetch
 * time would say "in 2 minutes" five minutes later.
 */
export function whenLabel2(publishAt: string, now: Date = new Date()): string {
  const at = new Date(publishAt);
  if (Number.isNaN(at.getTime())) return 'at an unreadable time';
  if (at.getTime() <= now.getTime()) return 'immediately';
  const mins = Math.round((at.getTime() - now.getTime()) / 60000);
  if (mins < 60) return `in ${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  return `on ${at.toISOString().slice(0, 10)}`;
}

/** A rendered block of an answer: a paragraph, or a bullet list. */
export type AnswerBlock = { kind: 'p'; text: string } | { kind: 'ul'; items: string[] };

/**
 * Split an answer into paragraphs and bullet lists.
 *
 * Exists because the answer arrives as one string and rendering it as one string is what
 * made it unreadable: 180 words of prose in a short panel, with no structure to lay out.
 * The prompt now asks for a lead sentence plus `- ` lines, and this turns those into real
 * blocks so CSS can space them.
 *
 * Deliberately NOT a markdown parser. The only structure asked for is bullets, and pulling
 * in a parser would mean bold, links and headings arriving unstyled from a model that was
 * told not to send them.
 */
export function answerBlocks(answer: string): AnswerBlock[] {
  const out: AnswerBlock[] = [];
  let para: string[] = [];
  let bullets: string[] = [];

  const flushPara = (): void => {
    if (para.length > 0) out.push({ kind: 'p', text: para.join(' ').trim() });
    para = [];
  };
  const flushBullets = (): void => {
    if (bullets.length > 0) out.push({ kind: 'ul', items: bullets });
    bullets = [];
  };

  for (const raw of answer.split('\n')) {
    const line = raw.trim();
    // Accept the dash, bullet and asterisk forms: the instruction says "- " but a model
    // drifts, and a stray "•" rendering as body text is the thing this function prevents.
    //
    // The trailing text is OPTIONAL. Requiring whitespace after the marker meant a bare
    // "-" on its own line was not a bullet, so it fell through and rendered INSIDE the
    // preceding paragraph as a stray dash. An empty bullet is still a bullet; it is just
    // dropped below.
    const bullet = /^[-•*](?:\s+(.*))?$/.exec(line);
    if (bullet) {
      flushPara();
      const item = (bullet[1] ?? '').trim();
      if (item !== '') bullets.push(item);
      continue;
    }
    if (line === '') {
      flushPara();
      flushBullets();
      continue;
    }
    flushBullets();
    para.push(line);
  }
  flushPara();
  flushBullets();
  return out;
}

/**
 * Snooze a chase until a date. `until` is an ISO instant.
 *
 * Note what is NOT sent: the baseline a drop is later measured against. The server reads that off
 * its own pipeline snapshot, because a browser-supplied timestamp would be a guess at a number the
 * server holds and a wrong one breaks the suppression silently.
 */
export async function snoozeChase(
  account: string,
  until: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  return postChase('/api/mo/chase/snooze', { account, until });
}

/** Drop a chase with a reason, which is shown back to him beside the row. */
export async function dropChase(
  account: string,
  reason: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  return postChase('/api/mo/chase/drop', { account, reason });
}

/**
 * One POST, one contract. Both chase calls report a failure in words he can act on, through the
 * same `networkReason` the chat lane uses: believing an account is snoozed when it is not is worse
 * than being told the click did not land.
 */
async function postChase(
  url: string,
  body: unknown,
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const b = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: b.error ?? `server returned ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: networkReason(err) };
  }
}

/**
 * Mark a commitment done.
 *
 * Confined to `mo/commitments/` server-side too: this route rewrites frontmatter, and an
 * arbitrary path would let it stamp `status: done` onto any note.
 */
export async function closeCommitment(path: string): Promise<{ ok: true } | { ok: false; error: string }> {
  let res: Response;
  try {
    res = await fetch('/api/mo/commitments/done', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ path }),
    });
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  if (res.ok) return { ok: true };
  let error = `could not close it (${res.status})`;
  try {
    const b = (await res.json()) as { error?: string };
    if (b.error) error = b.error;
  } catch {
    /* keep the default */
  }
  return { ok: false, error };
}

/**
 * Mark MANY commitments done in one request.
 *
 * Returns per-path results rather than one verdict, because partial success is the normal
 * outcome: a batch of forty that fails on one has genuinely closed thirty-nine, and reporting
 * that as an error would make him re-check the whole selection by hand.
 *
 * `ok: false` therefore means the REQUEST failed (network, auth, a malformed batch), not that
 * some notes did not close. Those come back in `failed`.
 */
export async function closeCommitments(
  paths: string[],
): Promise<
  | { ok: true; closed: string[]; failed: { path: string; error: string }[] }
  | { ok: false; error: string }
> {
  let res: Response;
  try {
    res = await fetch('/api/mo/commitments/done-many', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ paths }),
    });
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  if (!res.ok) {
    let error = `could not close them (${res.status})`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) error = b.error;
    } catch {
      /* keep the default */
    }
    return { ok: false, error };
  }
  try {
    const b = (await res.json()) as { closed?: unknown; failed?: unknown };
    return {
      ok: true,
      // Defensive rather than trusting: if the response shape drifts, closing NOTHING is the
      // safe reading. Treating an unreadable body as "all closed" would hide rows that are
      // still open.
      closed: Array.isArray(b.closed) ? (b.closed as string[]) : [],
      failed: Array.isArray(b.failed) ? (b.failed as { path: string; error: string }[]) : [],
    };
  } catch {
    return { ok: false, error: 'the result was not readable' };
  }
}

/**
 * Every commitment in one lane.
 *
 * The dashboard payload carries twelve per lane, so "show all 519" has to go and get the rest.
 * Without this the button would expand twelve rows and claim to be showing 519, which is worse
 * than not offering it.
 */
export async function fetchLane(
  lane: 'owed' | 'waiting',
): Promise<{ ok: true; items: DashboardCommitment[] } | { ok: false; error: string }> {
  let res: Response;
  try {
    res = await fetch(`/api/mo/commitments?lane=${lane}`, { credentials: 'same-origin' });
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  if (!res.ok) return { ok: false, error: `could not load the full list (${res.status})` };
  try {
    const body = (await res.json()) as { items?: unknown };
    return { ok: true, items: Array.isArray(body.items) ? (body.items as DashboardCommitment[]) : [] };
  } catch {
    return { ok: false, error: 'the list was not readable' };
  }
}

/** One note behind a count. */
export interface QueriedNote {
  title: string;
  path: string;
}

/**
 * The notes behind a number.
 *
 * The query is passed through OPAQUELY: it came from the server on a health row, and the client's
 * only job is to hand it back. Letting the UI compose or edit one would make the drill-down capable
 * of answering a different question from the headline it sits under, which is precisely the property
 * that makes an openable count trustworthy.
 */
export async function queryNotes(
  query: unknown,
): Promise<
  { ok: true; rows: QueriedNote[]; total: number; truncated: boolean } | { ok: false; error: string }
> {
  let res: Response;
  try {
    res = await fetch('/api/mo/query', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query }),
    });
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  let body: { rows?: unknown; total?: unknown; truncated?: unknown; error?: unknown };
  try {
    body = (await res.json()) as typeof body;
  } catch {
    return { ok: false, error: 'the answer was not readable' };
  }
  // A rejected query answers 400 with a sentence. Showing that beats an empty list, which would
  // read as "nothing matched".
  if (!res.ok) {
    return { ok: false, error: typeof body.error === 'string' ? body.error : `could not run that (${res.status})` };
  }
  return {
    ok: true,
    rows: Array.isArray(body.rows) ? (body.rows as QueriedNote[]) : [],
    total: typeof body.total === 'number' ? body.total : 0,
    truncated: body.truncated === true,
  };
}

/** One thing MO OS did to the vault. */
export interface VaultChange {
  id: number;
  at: string;
  actor: string;
  action: string;
  summary: string;
  noteCount: number;
  undone: boolean;
}

export async function fetchChanges(): Promise<VaultChange[]> {
  try {
    const res = await fetch('/api/mo/changes', { credentials: 'same-origin' });
    if (!res.ok) return [];
    const body = (await res.json()) as { items?: unknown };
    return Array.isArray(body.items) ? (body.items as VaultChange[]) : [];
  } catch {
    // A change log that fails to load must not break the deck around it.
    return [];
  }
}

export async function undoChange(id: number): Promise<{ ok: boolean; restored: number; reason: string }> {
  try {
    const res = await fetch(`/api/mo/changes/${id}/undo`, { method: 'POST', credentials: 'same-origin' });
    const body = (await res.json()) as { ok?: boolean; restored?: number; reason?: string; error?: string };
    return {
      ok: body.ok === true,
      restored: typeof body.restored === 'number' ? body.restored : 0,
      reason: body.reason ?? body.error ?? '',
    };
  } catch (err) {
    return { ok: false, restored: 0, reason: (err as Error).message };
  }
}

/**
 * The weekly operations picture, as the panel needs it.
 *
 * A NARROW CLIENT TYPE rather than importing the server's `WeeklyOps`. The payload carries
 * eleven sections built for the Monday brief; the panel renders three of them, and declaring
 * only those keeps a change to the server's internal shape from breaking the build over fields
 * nothing reads. `brief` is already ranked and capped on the server, so the client never
 * re-orders or truncates it.
 */
export interface WeeklyOpsView {
  generatedAt: string;
  headline: { open: number; unassigned: number; overdue: number };
  brief: string[];
  limitations: string[];
}

/**
 * Three outcomes, and they must stay distinguishable.
 *
 * `unavailable` is not `ok` with an empty brief. An operations panel rendering nothing reads as
 * "nothing is outstanding", which on a queue of 1,087 open items would be the exact fabrication
 * the deck exists to avoid. The panel says which of the two it is.
 */
export type WeeklyOpsResult =
  | { state: 'ok'; value: WeeklyOpsView }
  | { state: 'unavailable'; message: string };

export async function fetchWeeklyOps(): Promise<WeeklyOpsResult> {
  try {
    const res = await fetch('/api/mo/ops/weekly', { credentials: 'same-origin' });
    const body = (await res.json()) as Partial<WeeklyOpsView> & { error?: string };
    if (!res.ok) {
      return { state: 'unavailable', message: body.error ?? `Flow returned ${res.status}.` };
    }
    if (!Array.isArray(body.brief) || body.brief.length === 0) {
      return { state: 'unavailable', message: 'Flow answered with nothing to report.' };
    }
    return {
      state: 'ok',
      value: {
        generatedAt: typeof body.generatedAt === 'string' ? body.generatedAt : '',
        headline: {
          open: body.headline?.open ?? 0,
          unassigned: body.headline?.unassigned ?? 0,
          overdue: body.headline?.overdue ?? 0,
        },
        brief: body.brief,
        limitations: Array.isArray(body.limitations) ? body.limitations : [],
      },
    };
  } catch (err) {
    return { state: 'unavailable', message: (err as Error).message };
  }
}

/* ── Zou4Mo ─────────────────────────────────────────────────────────────────
   The composing surface. Types mirror `apps/server/src/mo/zou.ts`; they are declared here
   rather than shared because the server module is server-only and the client needs only the
   wire shape. */

export type MaterialKind =
  | 'proposal-outline'
  | 'follow-up-email'
  | 'pre-meeting-briefing'
  | 'talking-points'
  | 'deck-outline'
  | 'first-meeting-plan';
export type Brand = 'ntam' | 'vantage';

export interface MaterialBrief {
  kind: MaterialKind;
  about: string;
  brand: Brand;
  instruction?: string;
  history?: { question: string; answer: string }[];
}

export interface Material {
  kind: MaterialKind;
  brand: Brand;
  about: string;
  title: string;
  sections: { heading: string; body: string }[];
  citations: string[];
  caveats: string[];
}

/**
 * A draft that produced no sections is a FAILURE, not an empty success.
 *
 * The server returns 200 with empty sections and an explanatory caveat when the model did not
 * produce a usable document, because that is a caveat-worthy outcome rather than a server error.
 * Rendering it as a material would give him an empty page with a note under it; the tab says it
 * could not draft and shows the reason instead.
 */
export type DraftResult =
  | { state: 'done'; material: Material }
  /* `aborted` separates "you stopped it" from "it failed": only one is worth offering a retry. */
  | { state: 'failed'; message: string; aborted?: true };

export async function draftMaterial(brief: MaterialBrief, signal?: AbortSignal): Promise<DraftResult> {
  try {
    const res = await fetch('/api/mo/zou/materials', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(brief),
      signal,
    });
    const body = (await res.json()) as Partial<Material> & { error?: string };
    if (!res.ok) return { state: 'failed', message: body.error ?? `server returned ${res.status}` };
    if (!Array.isArray(body.sections) || body.sections.length === 0) {
      return {
        state: 'failed',
        message:
          Array.isArray(body.caveats) && body.caveats.length > 0
            ? body.caveats.join(' ')
            : 'the draft came back empty',
      };
    }
    return {
      state: 'done',
      material: {
        kind: brief.kind,
        brand: brief.brand,
        about: brief.about,
        title: typeof body.title === 'string' ? body.title : brief.about,
        sections: body.sections,
        citations: Array.isArray(body.citations) ? body.citations : [],
        caveats: Array.isArray(body.caveats) ? body.caveats : [],
      },
    };
  } catch (err) {
    if (signal?.aborted === true) return { state: 'failed', message: 'stopped', aborted: true };
    return { state: 'failed', message: networkReason(err) };
  }
}

/** Keep a draft. Separate from drafting on purpose: see the note in ChatOverlay.tsx. */
export async function saveMaterial(material: Material): Promise<{ ok: true; path: string } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/mo/zou/materials/save', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ material }),
    });
    const body = (await res.json()) as { ok?: boolean; path?: string; error?: string };
    if (!res.ok || body.ok !== true || typeof body.path !== 'string') {
      return { ok: false, error: body.error ?? `server returned ${res.status}` };
    }
    return { ok: true, path: body.path };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/** One Ask the Brain answer, as the chat overlay needs it. */
export interface BrainAnswer {
  answer: string;
  citations: string[];
  caveats: string[];
}

/**
 * Ask the Brain, for the chat overlay.
 *
 * `AskBrain.tsx` posts to the same route with its own local state; this is the version the
 * overlay uses. An empty answer is reported as a FAILURE rather than an empty bubble: the
 * server answers 200 with caveats when it retrieved nothing, and rendering that as a blank
 * reply would look like the question was ignored.
 */
/**
 * `signal` so a caller can actually stop this.
 *
 * A brain answer takes about 25 seconds. Without a signal the only way out was to close the
 * overlay, which left the request running, the model call paid for, and a `setState` landing on
 * an unmounted component. An abort is reported as its own outcome rather than an error, because
 * "you stopped it" and "it failed" are different things and only one of them is worth retrying.
 */
export async function askBrain(
  question: string,
  history: { question: string; answer: string }[] = [],
  signal?: AbortSignal,
): Promise<
  { ok: true; value: BrainAnswer } | { ok: false; error: string; aborted?: true }
> {
  try {
    const res = await fetch('/api/mo/ask', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ question, history }),
      signal,
    });
    const body = (await res.json()) as Partial<BrainAnswer> & { error?: string };
    if (!res.ok) return { ok: false, error: body.error ?? `server returned ${res.status}` };
    const answer = typeof body.answer === 'string' ? body.answer.trim() : '';
    const caveats = Array.isArray(body.caveats) ? body.caveats : [];
    if (answer === '') {
      return {
        ok: false,
        error: caveats.length > 0 ? caveats.join(' ') : 'nothing in the vault matched that.',
      };
    }
    return {
      ok: true,
      value: { answer, citations: Array.isArray(body.citations) ? body.citations : [], caveats },
    };
  } catch (err) {
    if (signal?.aborted === true) return { ok: false, error: 'stopped', aborted: true };
    return { ok: false, error: networkReason(err) };
  }
}

/**
 * What a thrown fetch actually means, in words he can act on.
 *
 * A bare `TypeError: Failed to fetch` is the most common failure here and the least useful
 * sentence in the app: it covers offline, DNS, a dead server and a blocked request, and it names
 * none of them. `navigator.onLine` is only trustworthy in the negative, which is exactly the case
 * worth separating.
 */
export function networkReason(err: unknown): string {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'you are offline, so nothing could be asked. It will work again when the connection is back.';
  }
  const msg = err instanceof Error ? err.message : String(err);
  if (/failed to fetch|networkerror|load failed/i.test(msg)) {
    return 'could not reach MO OS. The server may be restarting, which takes about half a minute.';
  }
  return msg;
}

/** One account's never-contacted people, as the panel needs them. */
export interface UntouchedAccountView {
  account: string;
  untouched: { name: string; email: string }[];
  totalInCrm: number;
  twentyUrl: string;
}

export type UntouchedResult =
  | { state: 'ok'; accounts: UntouchedAccountView[] }
  | { state: 'unavailable'; message: string };

/**
 * People at his live accounts nobody has ever written to.
 *
 * `unavailable` is distinct from an empty list, and the distinction is the whole point: "nobody
 * untouched anywhere" is a real and surprising answer, while "the CRM did not respond" is a
 * different fact that must not be rendered as the first one.
 */
export async function fetchUntouched(): Promise<UntouchedResult> {
  try {
    const res = await fetch('/api/mo/untouched', { credentials: 'same-origin' });
    const body = (await res.json()) as { accounts?: UntouchedAccountView[]; error?: string };
    if (!res.ok) return { state: 'unavailable', message: body.error ?? `server returned ${res.status}` };
    return { state: 'ok', accounts: Array.isArray(body.accounts) ? body.accounts : [] };
  } catch (err) {
    return { state: 'unavailable', message: (err as Error).message };
  }
}

/**
 * Open a drafted material as a branded deck in a new tab.
 *
 * Fetched as JSON and written into a new window rather than posted from a form: a form POST sends
 * urlencoded, the route reads JSON, and reconciling that would mean the endpoint accepting two
 * content types for no gain. The window is opened BEFORE the await, because a popup opened after
 * an async gap is not a user gesture any more and Safari blocks it.
 */
/**
 * Download a real PowerPoint of a drafted material.
 *
 * A .pptx cannot be shown in a tab, so this downloads. It reports the server's reason on failure
 * rather than falling back to the HTML deck: a deck that arrived in the wrong format looks
 * identical to one that arrived correctly until he opens it in front of somebody.
 */
export async function downloadPptx(material: Material): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const res = await fetch('/api/mo/zou/deck', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ material, format: 'pptx' }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: body.error ?? `server returned ${res.status}` };
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    /* The livery is in the FILENAME, because two decks for the same meeting in different brands
       otherwise arrive in Downloads with the same name and the second silently replaces the
       first. It is also the only label that survives being emailed on. */
    const stem = material.title.replace(/[^\w .-]/g, '').trim() || 'deck';
    a.download = `${stem} (${material.brand}).pptx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoked on a timeout rather than immediately: Safari cancels an in-flight download when the
    // object URL disappears from under it.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

export async function openDeck(material: Material): Promise<{ ok: true } | { ok: false; error: string }> {
  const win = globalThis.open('', '_blank');
  try {
    const res = await fetch('/api/mo/zou/deck', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ material }),
    });
    if (!res.ok) {
      win?.close();
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: body.error ?? `server returned ${res.status}` };
    }
    const html = await res.text();
    if (win === null) return { ok: false, error: 'the browser blocked the new tab' };
    win.document.open();
    win.document.write(html);
    win.document.close();
    return { ok: true };
  } catch (err) {
    win?.close();
    return { ok: false, error: (err as Error).message };
  }
}
